import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Layers,
  Info,
} from "lucide-react";

const GroupAnagramsVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Example 1",
      strs: ["eat", "tea", "tan", "ate", "nat", "bat"],
    },
    {
      id: 2,
      name: "Empty Strings",
      strs: ["", ""],
    },
    {
      id: 3,
      name: "Single Characters",
      strs: ["a"],
    },
    {
      id: 4,
      name: "No Anagrams",
      strs: ["abc", "def", "ghi"],
    },
    {
      id: 5,
      name: "Complex Case",
      strs: ["listen", "silent", "enlist", "google", "gogole"],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [strs, setStrs] = useState(defaultTestCases[0].strs);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function groupAnagrams(strs):", indent: 0 },
    { line: "groups = new Map()", indent: 1 },
    { line: "", indent: 0 },
    { line: "for str in strs:", indent: 1 },
    { line: "// Generate a unique key for anagrams", indent: 2 },
    { line: "key = str.split('').sort().join('')", indent: 2 },
    { line: "", indent: 0 },
    { line: "if !groups.has(key):", indent: 2 },
    { line: "groups.set(key, [])", indent: 3 },
    { line: "", indent: 0 },
    { line: "groups.get(key).push(str)", indent: 2 },
    { line: "", indent: 0 },
    { line: "return Array.from(groups.values())", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (strs) => {
    const allSteps = [];
    let stepCount = 0;
    let groups = new Map();

    const addStep = (description, highlightLine, details = {}) => {
      const groupsObj = Object.fromEntries(groups);
      allSteps.push({
        step: stepCount++,
        description,
        highlightLine,
        groups: groupsObj,
        ...details,
      });
    };

    addStep("Start: Initialize an empty map for groups.", 1);

    for (let i = 0; i < strs.length; i++) {
      const str = strs[i];
      addStep(`Processing string at index ${i}: "${str}".`, 3, {
        currentIndex: i,
      });

      const key = str.split("").sort().join("");
      addStep(`Generate sorted key for "${str}" -> "${key}".`, 5, {
        currentIndex: i,
        currentKey: key,
        currentStr: str,
      });

      if (!groups.has(key)) {
        addStep(`Key "${key}" not in map. Creating a new group.`, 7, {
          currentIndex: i,
          currentKey: key,
          currentStr: str,
        });
        groups.set(key, []);
        addStep(`New group created for key "${key}".`, 8, {
          currentIndex: i,
          currentKey: key,
          currentStr: str,
        });
      } else {
        addStep(`Key "${key}" found in map.`, 7, {
          currentIndex: i,
          currentKey: key,
          currentStr: str,
        });
      }

      const currentGroup = groups.get(key);
      currentGroup.push(str);
      addStep(`Adding "${str}" to group with key "${key}".`, 10, {
        currentIndex: i,
        currentKey: key,
        currentStr: str,
      });
    }

    addStep(
      "Finished processing all strings. Returning grouped anagrams.",
      12,
      { isFinal: true }
    );
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(strs);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [strs]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setStrs(testCases[index].strs);
  };

  const addCustomTestCase = () => {
    try {
      const parsedStrs = JSON.parse(customInput);
      if (
        !Array.isArray(parsedStrs) ||
        !parsedStrs.every((s) => typeof s === "string")
      ) {
        throw new Error("Input must be a valid JSON array of strings.");
      }
      const newTestCase = { id: Date.now(), name: `Custom`, strs: parsedStrs };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid format. ${error.message} Use: ["eat", "tea"]`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Layers className="text-blue-500" /> Group Anagrams
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an array of strings `strs`, group the anagrams together.
                You can return the answer in any order.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75 truncate">
                      [{tc.strs.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 5 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder='Custom: ["eat", "tea", "tan"]'
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4">
              <p className="text-sm font-semibold mb-2">Input Strings:</p>
              <div className="flex flex-wrap gap-2">
                {strs.map((str, idx) => (
                  <div
                    key={idx}
                    className={`px-3 py-1 rounded-full text-sm font-mono transition-all duration-300 ${
                      currentStepData?.currentIndex === idx
                        ? "bg-blue-500 text-white scale-110"
                        : "bg-gray-200"
                    }`}
                  >
                    {str || '""'}
                  </div>
                ))}
              </div>
            </div>

            {currentStepData?.currentStr && (
              <div className="my-4 p-2 bg-yellow-50 rounded-lg text-center">
                <p className="text-xs text-yellow-800">Processing</p>
                <p className="font-mono text-lg text-yellow-900">
                  "{currentStepData.currentStr}" ➔ "{currentStepData.currentKey}
                  "
                </p>
              </div>
            )}

            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700">
                Anagram Groups Map
              </h4>
              <div className="p-2 bg-gray-50 rounded mt-1 space-y-2 min-h-[100px]">
                {currentStepData &&
                  Object.entries(currentStepData.groups).map(([key, value]) => (
                    <div
                      key={key}
                      className={`p-2 rounded transition-all duration-300 ${
                        currentStepData.currentKey === key
                          ? "bg-blue-100"
                          : "bg-white"
                      }`}
                    >
                      <span className="font-mono text-xs font-bold text-blue-800 bg-blue-200 px-2 py-0.5 rounded-full">
                        {key}
                      </span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {value.map((s, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-gray-200 rounded-full text-xs font-mono"
                          >
                            {s || '""'}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {currentStepData?.isFinal && (
              <div className="mt-4 p-3 rounded-lg text-center bg-green-100">
                <div className="flex items-center justify-center font-bold text-green-700">
                  <CheckCircle className="mr-2" />
                  Finished Grouping
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The key to grouping anagrams is to find a common representation,
              or "key," for all strings that are anagrams of each other. A
              simple and effective key is the string itself, but with its
              characters sorted. We can use a hash map where keys are these
              sorted strings and values are lists of the original strings. We
              iterate through the input, generate the key for each string, and
              add it to the corresponding list in the map.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N * K log K)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(N * K)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N = number of strings, K = max length of a string.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  All anagrams become identical after their characters are
                  sorted alphabetically.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  A hash map is the perfect data structure to group items based
                  on a generated key.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  An alternative key generation method is to count character
                  frequencies (e.g., "a2b1c3") but sorting is often simpler to
                  implement.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="px-2 py-1 rounded-full bg-blue-500 text-white">
                  eat
                </div>
                <span>Current String</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded bg-yellow-50">"eat" ➔ "aet"</div>
                <span>Key Generation</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded bg-blue-100">...</div>
                <span>Active Group in Map</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupAnagramsVisualizer;
